import React from 'react';
import { ComponentType } from 'dto/raptor-component.dto';
import { ComponentManifestDto, ComponentVersionDto } from 'dto/raptor-component-version.dto';
import { useLoadingStates } from 'hooks/use-loading.hook';
import { observer } from 'mobx-react';
import { useRaptorComponentsStore } from 'store';
import { BasicCollapse, KeyValueDisplay, notification, Skeleton, Typography } from 'ui-lib';
import { ComponentVersionDisplayHelpers } from './component-version.helpers';
import css from '../raptor-components.css';
import { formatDate } from 'common-utils/date-utils';

interface IComponentVersionDetailsProps {
    accountId: number;
    componentVersionUuid: string;
}
interface ISpecificDetailsProps {
    manifests: ComponentManifestDto[];
    configuration: Record<string, unknown>;
}
const renderManifestPanels = (
    manifests: ComponentManifestDto[],
    renderManifestContent: (manifest: ComponentManifestDto) => React.ReactNode,
): JSX.Element => {
    if (!manifests.length) {
        return <Typography.Text type='secondary'>No manifests available.</Typography.Text>;
    }

    return (
        <BasicCollapse
            key={manifests.map((manifest, index) => `${manifest.platformUuid}-${index}`).join('|')}
            className={css['manifest-panel']}
            defaultActiveKey={manifests.map((manifest, index) => `${manifest.platformUuid || 'manifest'}-${index}`)}
            panels={manifests.map((manifest, index) => ({
                key: `${manifest.platformUuid || 'manifest'}-${index}`,
                header: manifest.platform?.displayName,
                content: renderManifestContent(manifest),
            }))}
        />
    );
};

const CatapultDetails = (props: ISpecificDetailsProps) => {
    const { configuration, manifests } = props;
    const renderPreformatted = (value: unknown): JSX.Element => (
        <pre className={css['details-preformatted']}>{ComponentVersionDisplayHelpers.formatDisplayValue(value)}</pre>
    );
    return (
        <React.Fragment>
            <Typography.Title level={5}>Configuration</Typography.Title>
            <pre className={css['configuration-payload']}>{JSON.stringify(configuration || {}, null, 2)}</pre>
            <Typography.Title level={5}>Manifests</Typography.Title>
            {renderManifestPanels(manifests, (manifest) => (
                <div className={css['details-section-content']}>
                    <KeyValueDisplay
                        column={1}
                        layout='vertical'
                        items={[
                            {
                                title: 'Install',
                                content: renderPreformatted(manifest.lifecycle?.install),
                            },
                            {
                                title: 'Run',
                                content: renderPreformatted(manifest.lifecycle?.run),
                            },
                            {
                                title: 'Startup',
                                content: renderPreformatted(manifest.lifecycle?.startup),
                            },
                            {
                                title: 'Artifacts',
                                content: ComponentVersionDisplayHelpers.renderRecords(manifest.uploadedFiles, 'fileName'),
                            }
                        ]}
                    />
                </div>
            ))}
        </React.Fragment>
    );
};
const DockerFileDetails = (props: ISpecificDetailsProps) => {
    const { configuration, manifests } = props;
    return (
        <React.Fragment>
            <Typography.Title level={5}>Manifests</Typography.Title>
            {renderManifestPanels(manifests, (manifest) => {
                return (
                    <div className={css['details-section-content']}>
                        <KeyValueDisplay
                            column={1}
                            items={[
                                {
                                    title: 'Artifact',
                                    content: ComponentVersionDisplayHelpers.renderRecords(manifest.uploadedFiles, 'fileName'),
                                },
                                {
                                    title: 'Docker Image',
                                    content: ComponentVersionDisplayHelpers.renderRecords(manifest.dockerImages, 'displayName'),
                                },
                            ]}
                        />
                    </div>
                );
            })}
        </React.Fragment>
    );
};

const DockerImageDetails = (props: ISpecificDetailsProps) => {
    const { configuration, manifests } = props;
    return (
        <React.Fragment>
            <Typography.Title level={5}>Manifests</Typography.Title>
            {renderManifestPanels(manifests, (manifest) => {
                return (
                    <div className={css['details-section-content']}>
                        <KeyValueDisplay
                            column={1}
                            items={[
                                {
                                    title: 'Docker Image',
                                    content: ComponentVersionDisplayHelpers.renderRecords(manifest.dockerImages, 'displayName'),
                                },
                            ]}
                        />
                    </div>
                );
            })}
        </React.Fragment>
    );
};

const DockerComposeDetails = (props: ISpecificDetailsProps) => {
    const { configuration, manifests } = props;
    return (
        <React.Fragment>
            <Typography.Title level={5}>Manifests</Typography.Title>
            {renderManifestPanels(manifests, (manifest) => {
                return (
                    <div className={css['details-section-content']}>
                        <KeyValueDisplay
                            column={1}
                            items={[
                                {
                                    title: 'Compose File',
                                    content: ComponentVersionDisplayHelpers.renderRecords(manifest.uploadedFiles, 'fileName'),
                                },
                                {
                                    title: 'Docker Images',
                                    content: ComponentVersionDisplayHelpers.renderRecords(manifest.dockerImages, 'displayName'),
                                },
                            ]}
                        />
                    </div>
                );
            })}
        </React.Fragment>
    );
};

const renderSpecificDetails = {
    [ComponentType.CATAPULT]: CatapultDetails,
    [ComponentType.DOCKER_FILE]: DockerFileDetails,
    [ComponentType.DOCKER_IMAGE]: DockerImageDetails,
    [ComponentType.DOCKER_COMPOSE]: DockerComposeDetails,
};

export const ComponentVersionDetails = observer((props: IComponentVersionDetailsProps): JSX.Element => {
    const { accountId, componentVersionUuid } = props;
    const raptorComponentsStore = useRaptorComponentsStore();
    const loadingHook = useLoadingStates();
    const [componentVersion, setComponentVersion] = React.useState<ComponentVersionDto>();

    React.useEffect(() => {
        let isMounted = true;

        const loadDetails = async (): Promise<void> => {
            const result = await loadingHook.withLoading(async () => {
                return raptorComponentsStore.getComponentVersion(accountId, componentVersionUuid);
            });

            if (!isMounted) {
                return;
            }

            if (result.isFail()) {
                notification.error({
                    message: 'Failed to load component version details',
                    description: result.errorMessage,
                });
                return;
            }

            setComponentVersion(result.getOrThrow());
        };

        loadDetails();

        return () => {
            isMounted = false;
        };
    }, [accountId, componentVersionUuid]);

    if (loadingHook.loading) {
        return <Skeleton loading={true} />;
    }

    if (!componentVersion) {
        return <Typography.Text type='secondary'>Component version details were not found.</Typography.Text>;
    }

    const configuration = componentVersion.configuration || {};
    const manifests = componentVersion.manifests || [];

    const Component = renderSpecificDetails[componentVersion.componentType];
    return (
        <div className={css['component-version-details']}>
            <div className={css['details-summary']}>
                <KeyValueDisplay
                column={1}
                    items={[
                        {
                            title: 'Component',
                            content: componentVersion.componentName,
                        },
                        {
                            title: 'Version',
                            content: componentVersion.version,
                        },
                        {
                            title: 'Description',
                            content: componentVersion.description,
                        },
                        {
                            title: 'Type',
                            content: componentVersion.componentType,
                        },
                        {
                            title: 'Status',
                            content: ComponentVersionDisplayHelpers.renderStatus(componentVersion.status),
                        },
                    ]}
                />
            </div>
            <Component manifests={manifests} configuration={configuration} />
        </div>
    );
});
